import { useFaceArt, type FaceArt, type FaceKind } from '../lib/faceArt';
import type { MascotaFace } from '../types';
import { MascotBody } from './MascotBody';
import type { MascotMood } from './Mascot';

/**
 * La cara de una mascota. Si su dibujo trae la cara de la lámina (lib/faceArt.ts) se
 * muestra esa, una expresión entera por estado de ánimo; si no, se dibuja con la
 * configuración del admin (`face`), en capas propias para poder animarla.
 */
export function FaceLayer({ bodyUrl, face, clip, mood }: { bodyUrl: string; face: MascotaFace; clip: string; mood: MascotMood }) {
  const art = useFaceArt(bodyUrl);
  if (art === undefined) return null; // cargándose
  return art ? <TracedFace art={art} mood={mood} /> : <MascotFace face={face} clip={clip} mood={mood} />;
}

/* ── La de la lámina ───────────────────────────────────────────────────── */

const NO_REPAINT: Record<string, string> = {};

/** Qué cara de la lámina corresponde a cada estado. Hablando es la del frente con la boca en movimiento. */
const FACE_OF: Record<MascotMood, FaceKind> = {
  idle: 'idle', talking: 'idle', happy: 'happy', oops: 'uy', thinking: 'pensando',
};

function TracedFace({ art, mood }: { art: FaceArt; mood: MascotMood }) {
  const cara = art[FACE_OF[mood]];
  return (
    <g className="m-face m-face-art">
      <MascotBody root={cara.cejas} colors={NO_REPAINT} />
      {/* Parpadea con `.m-eye` y mira de costado con `.m-glance-art`: cada uno con su transform. */}
      <g className="m-eye">
        <g className="m-glance-art">
          <MascotBody root={cara.ojos} colors={NO_REPAINT} />
        </g>
      </g>
      <MascotBody root={cara.nariz} colors={NO_REPAINT} />
      {mood === 'talking' ? (
        // Tres bocas de la lámina que se turnan (abierta, redonda, cerrada), como la dibujada.
        <>
          <g className="m-mouth-open"><MascotBody root={art.happy.boca} colors={NO_REPAINT} /></g>
          <g className="m-mouth-round"><MascotBody root={art.uy.boca} colors={NO_REPAINT} /></g>
          <g className="m-mouth-smile"><MascotBody root={art.idle.boca} colors={NO_REPAINT} /></g>
        </>
      ) : (
        <MascotBody root={cara.boca} colors={NO_REPAINT} />
      )}
    </g>
  );
}

/* ── La dibujada con la configuración ──────────────────────────────────── */

/** Ojo izquierdo centrado en (0,0); el derecho es el mismo espejado. */
const SCLERA = 'M-34,2 C-22,-16 20,-18 34,-2 C22,12 -22,14 -34,2 Z';

function Eye({ x, y, scale, browY, flip, clip }: {
  x: number; y: number; scale: number; browY: number; flip?: boolean; clip: string;
}) {
  return (
    <g transform={`translate(${x},${y}) scale(${flip ? -scale : scale},${scale})`}>
      {/* El desplazamiento va en un <g>: el transform de CSS de las
          expresiones pisaría el atributo si estuvieran en el mismo elemento. */}
      <g transform={`translate(0,${browY})`}>
        <path className="m-brow" d="M-32,-28 C-18,-40 12,-42 30,-33" />
      </g>
      <g className="m-eye">
        <path className="m-eye-white" d={SCLERA} />
        <g clipPath={`url(#${clip})`}>
          <g className="m-pupil">
            <circle className="m-ink" cx="2" cy="0" r="15" />
            <circle className="m-eye-white" cx="7" cy="-5" r="4.5" />
          </g>
        </g>
        <path className="m-lid" d="M-38,4 C-24,-20 22,-22 38,-4 M-36,1 L-44,-5" />
      </g>
    </g>
  );
}

export function MascotFace({ face, clip, mood }: { face: MascotaFace; clip: string; mood: MascotMood }) {
  const midX = face.eyeX + face.eyeGap / 2;
  const eye = { y: face.eyeY, scale: face.eyeScale, browY: face.browY, clip };
  return (
    <g className="m-face">
      <defs>
        <clipPath id={clip}>
          <path d={SCLERA} />
        </clipPath>
      </defs>
      {face.cheeks && [-1, 1].map((side) => (
        <ellipse
          key={side}
          className="m-cheek"
          cx={midX + side * face.cheekGap / 2}
          cy={face.cheekY}
          rx={20 * face.cheekScale}
          ry={11 * face.cheekScale}
        />
      ))}
      <Eye x={face.eyeX} {...eye} />
      <Eye x={face.eyeX + face.eyeGap} flip {...eye} />
      <g transform={`translate(${face.mouthX},${face.mouthY}) scale(${face.mouthScale})`}>
        <Mouth mood={mood} />
      </g>
    </g>
  );
}

const SMILE = (
  <g className="m-mouth-smile">
    <path className="m-lip" d="M-26,4 C-12,16 12,16 26,4 C12,9 -12,9 -26,4 Z" />
    <path className="m-line" d="M-40,-6 C-20,10 20,10 40,-6" />
  </g>
);

function Mouth({ mood }: { mood: MascotMood }) {
  switch (mood) {
    case 'talking':
      return (
        <>
          <g className="m-mouth-open">
            <path className="m-mouth" d="M-22,-6 C-10,-10 10,-10 22,-6 C20,16 8,22 0,22 C-8,22 -20,16 -22,-6 Z" />
            <ellipse className="m-tongue" cx="0" cy="15" rx="10" ry="5" />
          </g>
          <ellipse className="m-mouth m-mouth-round" cx="0" cy="4" rx="12" ry="14" />
          {SMILE}
        </>
      );
    case 'happy':
      return (
        <g>
          <path className="m-mouth" d="M-40,-8 C-20,-2 20,-2 40,-8 C34,26 14,36 0,36 C-14,36 -34,26 -40,-8 Z" />
          <path className="m-eye-white" d="M-33,-4 C-16,1 16,1 33,-4 L30,5 C14,9 -14,9 -30,5 Z" />
          <ellipse className="m-tongue" cx="0" cy="26" rx="15" ry="7" />
          <path className="m-line m-line-thin" d="M-40,-8 C-20,-2 20,-2 40,-8 C34,26 14,36 0,36 C-14,36 -34,26 -40,-8 Z" />
        </g>
      );
    case 'oops':
      return <ellipse className="m-mouth" cx="0" cy="6" rx="9" ry="11" />;
    case 'thinking':
      return <path className="m-line" d="M-22,6 C-8,2 8,4 24,-2" />;
    default:
      return SMILE;
  }
}
